import g from '#core/global';
import events from '#core/events';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';


const promo_$ = '.simple-promo';
const inner_$ = '.simple-promo__inner';
const bars_$ = '[data-simple-promo-bars]';
const bar_class = 'simple-promo__bar';
const initialized_attr = 'data-bars-initialized';

const bar_width_px = 20;
const bar_min_gap_px = 26;
const bar_min_height_px = 240;
const bar_max_height_px = 640;
const bar_in_class = 'simple-promo__bar--in';

let has_simple_promo = false;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('simple-promo')) return false;

		load.content(() => {
			has_simple_promo = assign_elements();
		});
		load.all(() => {
			has_simple_promo = assign_elements();
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_simple_promo) return true;

	const $bars_containers = document.querySelectorAll(bars_$);
	if (!$bars_containers.length) return false;

	check_all();

	window.addEventListener(g.scroll_e, util.throttle(check_all, 200));
	window.addEventListener(g.resize_e, util.throttle(check_all, 250));

	return true;
}


/**
 * Iterate uninitialized bar containers and build bars when in view.
 */
function check_all() {
	document.querySelectorAll(bars_$).forEach($container => {
		if ($container.getAttribute(initialized_attr) === g.true_s) return false;

		const is_visible = layout.isIntersecting({
			$element: $container,
			lookaround: 1,
		});
		if (!is_visible) return false;

		$container.setAttribute(initialized_attr, g.true_s);
		build_bars($container);
	});
}


/**
 * Build bars: 20px wide, ≥26px gap, first flush left, last flush right.
 * Each bar has a random height and a final top where its bottom extends at
 * or beyond the container edge (clipped by overflow:hidden). Center bars
 * favor the lower 50% of the available top range; edge bars sample uniformly.
 * @param {element} $container
 */
function build_bars($container) {
	const width = layout.getWidth($container, false);
	const container_height = layout.getHeight($container, false);
	if (!width || !container_height) return false;

	const total_unit = bar_width_px + bar_min_gap_px;
	const count = Math.floor((width - bar_width_px) / total_unit) + 1;
	if (count < 2) return false;

	const usable = width - bar_width_px;
	const gap = usable / (count - 1);

	const fragment = document.createDocumentFragment();
	const bars = [];
	for (let i = 0; i < count; i++) {
		const $bar = document.createElement('div');
		$bar.className = bar_class;

		const left = Math.round(i * gap);

		// edge_factor: 1 at horizontal center, 0 at left/right edges.
		// Smoothstep applied so the bias eases in at the edges and out at the
		// center, producing a gentle U-shaped silhouette rather than a sharp V.
		const edge_factor_linear = 1 - Math.abs((i / (count - 1)) * 2 - 1);
		const edge_factor = edge_factor_linear * edge_factor_linear * (3 - 2 * edge_factor_linear);

		// Height bias: center bars (edge_factor=1) sample only from the lower 30%
		// of the height range; edge bars sample uniformly across the full range.
		const height_high_bound = 1 - edge_factor * 0.7;
		const h_t = Math.random() * height_high_bound;
		const height = Math.round(bar_min_height_px + h_t * (bar_max_height_px - bar_min_height_px));

		// Bottom of bar must not be visible: top >= container_height - height.
		// Max top keeps at least 40px of the bar visible inside the container.
		const min_top = Math.max(0, container_height - height);
		const max_top = container_height - 40;
		const range = Math.max(0, max_top - min_top);

		// Top bias: center bars sample only from the upper 40% of the range
		// (i.e. pushed downward); edge bars sample only from the lower 40% of
		// the range (i.e. pushed upward); bars between blend linearly.
		const edge_inverse = 1 - edge_factor;
		const low_bound = edge_factor * 0.6;
		const high_bound = 1 - edge_inverse * 0.35;
		const t = low_bound + Math.random() * (high_bound - low_bound);
		const top = Math.round(min_top + t * range);

		$bar.style.setProperty('--bar-left', `${left}px`);
		$bar.style.setProperty('--bar-height', `${height}px`);
		$bar.style.setProperty('--bar-top', `${top}px`);
		$bar.style.setProperty('--bar-delay-mult', Math.random().toFixed(3));

		fragment.appendChild($bar);
		bars.push($bar);
	}
	$container.appendChild(fragment);

	// Force reflow so translate:0 100% is established before the transition fires.
	$container.offsetHeight;

	// Wait for the sibling content to start revealing before animating bars in.
	// The .simple-promo__inner element uses [target=children], so its descendants
	// dispatch reveal-start on themselves; we catch those via capture phase since
	// CustomEvent does not bubble.
	const trigger_in = () => {
		requestAnimationFrame(() => {
			bars.forEach($bar => {
				$bar.classList.add(bar_in_class);
			});
		});
	};

	const $promo = $container.closest(promo_$);
	const $inner = $promo && $promo.querySelector(inner_$);
	if (!$inner) {
		trigger_in();
		return true;
	}

	// If any descendant has already started or finished revealing, trigger now.
	const already_revealing = $inner.querySelector(
		`[${g.revealing_attr}], [${g.revealed_attr}]`,
	);
	if (already_revealing) {
		trigger_in();
		return true;
	}

	$inner.addEventListener(events.reveal.start, trigger_in, {
		once: true,
		capture: true,
	});

	return true;
}
